// Pure helpers for the analytics pages (goals/admin-panel/DESIGN.md
// "Analytics read API"): compact numbers, week-over-week changes, sparkline
// geometry and the Overview's status strip.
import type { AdminSchemas } from "@10twenty/api/admin";

export type AnalyticsMetric = AdminSchemas["AnalyticsMetric"];
export type AnalyticsSeries = AdminSchemas["AnalyticsSeries"];
export type AnalyticsOverview = AdminSchemas["AnalyticsOverview"];

/** 1,284 / 12.9K / 4.2M: grouped below 10,000, then one decimal (dropped once the number has three digits). */
export function compact(value: number, locale?: string): string {
  const abs = Math.abs(value);
  if (abs < 10_000) return Math.round(value).toLocaleString(locale);
  const [scaled, suffix] = abs < 1e6 ? [value / 1e3, "K"] : abs < 1e9 ? [value / 1e6, "M"] : [value / 1e9, "B"];
  const digits = Math.abs(scaled) >= 100 ? 0 : 1;
  return `${scaled.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: digits })}${suffix}`;
}

/** A 0–1 ratio as a whole percent ("—" when there is none). */
export function percent(ratio: number | null): string {
  return ratio === null ? "—" : `${Math.round(ratio * 100)}%`;
}

/** The only series of an ungrouped read, or an empty one. */
export function totals(series: AnalyticsSeries | undefined): (number | null)[] {
  return series?.series[0]?.values ?? [];
}

export interface Change {
  current: number;
  previous: number;
}

/**
 * The last 7 buckets against the 7 before (sums for events), or the latest
 * value against the one 7 buckets earlier (levels); null without enough data.
 */
export function weekChange(values: readonly (number | null)[], combine: AnalyticsSeries["combine"]): Change | null {
  if (combine === "sum") {
    if (values.length < 14) return null;
    const window = (from: number) => values.slice(from, from + 7);
    const [previous, current] = [window(values.length - 14), window(values.length - 7)];
    if (previous.some((v) => v === null) || current.some((v) => v === null)) return null;
    const sum = (part: (number | null)[]) => part.reduce<number>((total, v) => total + (v ?? 0), 0);
    return { current: sum(current), previous: sum(previous) };
  }
  const current = values.at(-1);
  const previous = values.at(-8);
  if (current === null || current === undefined || previous === null || previous === undefined) return null;
  return { current, previous };
}

/** "+12% vs previous 7 days", "no change …", "new …" (from zero). */
export function changeText(change: Change, period = "7 days earlier"): string {
  const { current, previous } = change;
  if (current === previous) return `no change vs ${period}`;
  if (previous === 0) return `+${compact(current)} vs ${period}`;
  const pct = Math.round(((current - previous) / previous) * 100);
  return `${pct > 0 ? "+" : pct < 0 ? "−" : "±"}${Math.abs(pct)}% vs ${period}`;
}

export interface Sparkline {
  /** One polyline `points` string per run of known values (a null breaks the line). */
  runs: string[];
  /** The latest known value's point, drawn in the accent. */
  last: { x: number; y: number } | null;
  /** Each bucket's x, for hover targets. */
  xs: number[];
  min: number;
  max: number;
}

/**
 * Sparkline geometry in a `width` × `height` box with `pad` inside the edges:
 * x spreads the buckets evenly, y spans min–max (a flat line sits mid-height).
 */
export function sparkline(values: readonly (number | null)[], width: number, height: number, pad = 2): Sparkline {
  const known = values.filter((v): v is number => v !== null);
  const min = known.length ? Math.min(...known) : 0;
  const max = known.length ? Math.max(...known) : 0;
  const step = values.length > 1 ? (width - 2 * pad) / (values.length - 1) : 0;
  const xs = values.map((_, i) => (values.length > 1 ? pad + i * step : width / 2));
  const y = (v: number) => (max === min ? height / 2 : pad + ((max - v) / (max - min)) * (height - 2 * pad));
  const round = (n: number) => Math.round(n * 100) / 100;
  const runs: string[] = [];
  let run: string[] = [];
  let last: Sparkline["last"] = null;
  values.forEach((v, i) => {
    if (v === null) {
      if (run.length) runs.push(run.join(" "));
      run = [];
      return;
    }
    last = { x: round(xs[i]!), y: round(y(v)) };
    run.push(`${last.x},${last.y}`);
  });
  if (run.length) runs.push(run.join(" "));
  return { runs, last, xs: xs.map(round), min, max };
}

export type StatusState = "ok" | "warning" | "failing";
export interface StatusItem {
  label: string;
  state: StatusState;
  text: string;
}

/** The status strip: each check with its state and words (never colour alone). */
export function statusItems(status: AnalyticsOverview["status"], dayLabel: (day: string) => string): StatusItem[] {
  const check = (label: string, value: "ok" | "failing"): StatusItem => ({
    label,
    state: value === "ok" ? "ok" : "failing",
    text: value === "ok" ? "OK" : "Failing",
  });
  const built = status.analytics_built_through;
  return [
    check("Database", status.database),
    check("Migrations", status.migrations),
    {
      label: "Analytics",
      state: status.analytics === "ok" ? "ok" : "warning",
      text:
        built === null
          ? "Not built yet"
          : status.analytics === "ok"
            ? `Through ${dayLabel(built)}`
            : `Behind: through ${dayLabel(built)}`,
    },
    {
      label: "Live sockets",
      state: "ok",
      text: status.connections.toLocaleString(),
    },
  ];
}
